import {
  makeStyles,
  IconButton,
  Tooltip,
  withStyles,
  Link as MaterialLink,
  CircularProgress,
} from '@material-ui/core';
import EventAvailableIcon from '@material-ui/icons/EventAvailable';
import { format } from 'date-fns';
import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import eventsAPI, { Event } from '../../api/events';
import { Venture } from '../../api/ventures/types/Venture';
import { Pages } from '../../router/constants';
import { COLORS } from '../../theme/variables';
import { encodeQuery } from '../../utils/functions';
import { Link, Text } from '../common';

enum RelevantSessionsIconState {
  IDLE = 'IDLE',
  LOADING = 'LOADING',
  ERROR = 'ERROR',
  SUCCESS = 'SUCCESS',
}

interface RelevantSessionsIconProps {
  venture: Venture;
}

interface RelevantSessionsTooltipProps {
  state: RelevantSessionsIconState;
  sessions: Event[];
  onLoad: () => void;
}

const STooltip = withStyles(() => ({
  tooltip: {
    width: 300,
    padding: 16,
    backgroundColor: COLORS.COLOR_ADDITIONAL_WHITE,
    boxShadow:
      '0px 2px 4px rgba(34, 91, 187, 0.2), 0px 4px 8px rgba(51, 126, 255, 0.12)',
    boxSizing: 'border-box',
  },
}))(Tooltip);

const useStyles = makeStyles(() => ({
  link: {
    '&:hover': {
      borderBottom: 'none',
    },
  },
  wrapper: {
    width: '100%',
  },
  loader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  item: {
    minWidth: 30,
    backgroundColor: COLORS.COLOR_GRAY_LIGHTENED_30,
    textAlign: 'center',
    borderRadius: 16,
    padding: 7,
  },
}));

function RelevantSessionsTooltip({
  state,
  sessions,
  onLoad,
}: RelevantSessionsTooltipProps) {
  const classes = useStyles();

  useEffect(() => {
    onLoad();
  }, [onLoad]);

  const sessionsData = useMemo(() => {
    const getDateString = (session: Event) => {
      try {
        const startDate = new Date(session.start);
        return format(startDate, 'MM/dd/yyyy hh:mm a');
      } catch (error) {
        return 'Invalid date';
      }
    };

    return sessions.map((session) => {
      const dateString = getDateString(session);
      return {
        ...session,
        dateString,
      };
    });
  }, [sessions]);

  return (
    <div className={classes.wrapper}>
      {state === RelevantSessionsIconState.LOADING ? (
        <div className={classes.loader}>
          <CircularProgress size={24} color='primary' />
        </div>
      ) : state === RelevantSessionsIconState.ERROR ? (
        <Text variant='normal'>Something went wrong...</Text>
      ) : !sessionsData.length ? (
        <Text variant='normal'>No sessions</Text>
      ) : (
        <div className={classes.list}>
          {sessionsData.map((session) => (
            <div
              key={`venture-relevant-sessions-${session.id}`}
              className={classes.item}>
              <Text variant='normal'>{session.dateString}</Text>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RelevantSessionsIcon({ venture }: RelevantSessionsIconProps) {
  const classes = useStyles();
  const location = useLocation();

  const [sessions, setSessions] = useState<Event[]>([]);
  const [state, setState] = useState<RelevantSessionsIconState>(
    RelevantSessionsIconState.IDLE,
  );

  const search = useMemo(() => {
    return `filter=${encodeQuery({
      venture: { id: venture.id, ventureName: venture.ventureName },
    })}`;
  }, [venture.id, venture.ventureName]);

  const fetchRelevantSessions = useCallback(async () => {
    if (state !== RelevantSessionsIconState.IDLE) {
      return;
    }

    try {
      setState(RelevantSessionsIconState.LOADING);
      const newSessions = await eventsAPI.searchByFilterV2({
        ventureId: venture.id,
      });
      setSessions(newSessions);
      setState(RelevantSessionsIconState.SUCCESS);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      setState(RelevantSessionsIconState.ERROR);
    }
  }, [state, venture.id]);

  return (
    <STooltip
      title={
        <RelevantSessionsTooltip
          state={state}
          sessions={sessions}
          onLoad={fetchRelevantSessions}
        />
      }>
      <MaterialLink
        data-testid='venture-sessions-link-icon'
        onClick={(e: SyntheticEvent<any>) => e.stopPropagation()}
        component={Link}
        className={classes.link}
        to={{
          pathname: Pages.SESSIONS,
          search,
          state: {
            prevPath: `${location.pathname}${location.search}`,
          },
        }}>
        <IconButton data-testid='founder-notes-icon'>
          <EventAvailableIcon />
        </IconButton>
      </MaterialLink>
    </STooltip>
  );
}

export default RelevantSessionsIcon;
